import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      localStorage.setItem("adminToken", response.data.access_token);
      toast.success("Login successful");
      navigate("/admin/dashboard");
    } catch (err) {
      setError("Invalid credentials. Please try again.");
      toast.error("Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 border border-amber-400/50 flex items-center justify-center mx-auto mb-6">
            <span className="text-amber-400 font-bold text-2xl">UV</span>
          </div>
          <h1 className="text-2xl text-white mb-2">Admin Portal</h1>
          <p className="text-slate-500 text-sm">Unleashed Ventures Management</p>
        </div>

        {/* Login Form */}
        <div className="glass-card p-8">
          <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 p-3 border border-red-400/20">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <div>
              <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">
                Username
              </label>
              <input
                type="text"
                data-testid="admin-username-input"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                required
                className="luxury-input w-full"
                placeholder="Enter username"
              />
            </div>

            <div>
              <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">
                Password
              </label>
              <input
                type="password"
                data-testid="admin-password-input"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                required
                className="luxury-input w-full"
                placeholder="Enter password"
              />
            </div>

            <button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 text-[#08090D] hover:bg-amber-400 font-bold uppercase tracking-widest text-xs px-8 py-4 transition-all duration-300 btn-gold-glow disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 loading-spinner" />
                  Authenticating...
                </>
              ) : (
                <>
                  <Lock size={14} />
                  Sign In
                </>
              )}
            </button>
          </form>
        </div>

        {/* Back to site */}
        <div className="text-center mt-8">
          <a
            href="/"
            className="text-slate-500 hover:text-amber-400 text-sm transition-colors"
          >
            ← Back to Website
          </a>
        </div>
      </motion.div>
    </main>
  );
}
